import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import ClientSwitcher from "@/components/ClientSwitcher";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, XCircle, Circle, Pill, Plus, Trash2, Info } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const STATUSES = ["administered", "missed", "none"];

function StatusButton({ value, onClick, slot, medId }) {
  const baseClass = "flex-1 h-12 rounded-md border-2 flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors";
  const map = {
    administered: "border-emerald-600 bg-emerald-50 text-emerald-700",
    missed: "border-red-600 bg-red-50 text-red-700",
    none: "border-border bg-white text-muted-foreground",
  };
  const labels = { administered: "Given", missed: "Missed", none: "—" };
  const Icon = value === "administered" ? CheckCircle2 : value === "missed" ? XCircle : Circle;
  return (
    <button type="button" onClick={onClick} className={`${baseClass} ${map[value]}`} data-testid={`emar-${slot}-${medId}`}>
      <Icon className="w-4 h-4" />
      {labels[value]}
    </button>
  );
}

export default function EMARForm() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [allMeds, setAllMeds] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [entries, setEntries] = useState({}); // medId -> {am_status, pm_status, missed_reason}
  const [prnEntries, setPrnEntries] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/clients").then(({ data }) => {
      setClients(data);
      const saved = localStorage.getItem("oppo_active_client");
      setClientId(data.find((c) => c.id === saved)?.id || data[0]?.id || "");
    });
  }, []);

  useEffect(() => {
    if (!clientId) return;
    localStorage.setItem("oppo_active_client", clientId);
    api.get(`/medications?client_id=${clientId}`).then(({ data }) => {
      setAllMeds(data);
      const next = {};
      data.filter((m) => !m.is_prn).forEach((m) => {
        next[m.id] = { am_status: "none", pm_status: "none", missed_reason: "" };
      });
      setEntries(next);
      setPrnEntries([]);
    });
  }, [clientId]);

  const routineMeds = useMemo(() => allMeds.filter((m) => !m.is_prn), [allMeds]);
  const prnMeds = useMemo(() => allMeds.filter((m) => m.is_prn), [allMeds]);
  const isInactive = allMeds.length === 0;

  const cycleStatus = (medId, slot) => {
    setEntries((e) => {
      const cur = e[medId]?.[slot] || "none";
      const nextIdx = (STATUSES.indexOf(cur) + 1) % STATUSES.length;
      return { ...e, [medId]: { ...e[medId], [slot]: STATUSES[nextIdx] } };
    });
  };

  const setMissedReason = (medId, reason) => {
    setEntries((e) => ({ ...e, [medId]: { ...e[medId], missed_reason: reason } }));
  };

  const selectAll = (slot, status) => {
    const next = { ...entries };
    routineMeds.forEach((m) => { next[m.id] = { ...next[m.id], [slot]: status }; });
    setEntries(next);
  };

  const addPrn = () => setPrnEntries([...prnEntries, { id: crypto.randomUUID(), medication_id: "", name: "", dosage: "", time: "", reason: "" }]);
  const updatePrn = (id, patch) => setPrnEntries((arr) => arr.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const removePrn = (id) => setPrnEntries((arr) => arr.filter((p) => p.id !== id));

  const summary = useMemo(() => {
    let given = 0, missed = 0;
    routineMeds.forEach((m) => {
      const e = entries[m.id] || {};
      ["am_status", "pm_status"].forEach((s) => {
        if (e[s] === "administered") given++;
        if (e[s] === "missed") missed++;
      });
    });
    return { given, missed };
  }, [routineMeds, entries]);

  const submit = async () => {
    if (!clientId) return toast.error("Select a client");
    // Validate missed reasons
    for (const m of routineMeds) {
      const e = entries[m.id] || {};
      if ((e.am_status === "missed" || e.pm_status === "missed") && !e.missed_reason?.trim()) {
        return toast.error(`Provide a reason for missed dose of ${m.name}`);
      }
    }
    // Validate PRN entries
    for (const p of prnEntries) {
      if (!p.medication_id) return toast.error("Pick a PRN medication for each PRN entry");
      if (!p.reason?.trim()) return toast.error("PRN entries require a reason");
    }
    setSubmitting(true);
    try {
      await api.post("/emar-logs", {
        client_id: clientId,
        date,
        entries: routineMeds.map((m) => ({
          medication_id: m.id,
          name: m.name,
          dosage: m.dosage,
          am_status: entries[m.id]?.am_status || "none",
          pm_status: entries[m.id]?.pm_status || "none",
          missed_reason: entries[m.id]?.missed_reason || "",
        })),
        prn_entries: prnEntries.map((p) => {
          const med = prnMeds.find((m) => m.id === p.medication_id);
          return {
            medication_id: p.medication_id,
            name: med?.name || p.name,
            dosage: med?.dosage || p.dosage,
            time: p.time,
            reason: p.reason,
          };
        }),
      });
      toast.success("eMAR submitted");
      navigate("/provider");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="emar-form">
      <div>
        <p className="text-label">eMAR</p>
        <h2 className="font-display text-2xl font-bold tracking-tight">AM / PM Medication Log</h2>
        <p className="text-sm text-muted-foreground">Tap status to cycle: — → Given → Missed</p>
      </div>

      <ClientSwitcher clients={clients} activeId={clientId} onChange={setClientId} />

      {isInactive ? (
        <Card className="p-8 bg-secondary/40 border-dashed border-2" data-testid="emar-inactive">
          <div className="text-center">
            <Pill className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-60" />
            <p className="font-display font-semibold">eMAR Inactive</p>
            <p className="text-sm text-muted-foreground mt-1">
              This client has no assigned medications. The eMAR module is disabled.
            </p>
            <p className="text-xs text-muted-foreground mt-3">Ask your admin to add medications via Contractor / Client Profiles → Add Medication.</p>
          </div>
        </Card>
      ) : (
        <>
          <Card className="p-4 space-y-3">
            <div>
              <Label htmlFor="emar-date" className="text-label">Date</Label>
              <Input id="emar-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-12 mt-1" data-testid="emar-date" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" className="h-12" onClick={() => selectAll("am_status", "administered")} data-testid="select-all-am">Select All AM Given</Button>
              <Button type="button" variant="outline" className="h-12" onClick={() => selectAll("pm_status", "administered")} data-testid="select-all-pm">Select All PM Given</Button>
            </div>
          </Card>

          <Card className="p-4">
            <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_140px_140px] gap-2 items-center pb-2 border-b border-border mb-2">
              <p className="text-label">Routine Medication</p>
              <p className="text-label text-center w-[120px] sm:w-auto">AM</p>
              <p className="text-label text-center w-[120px] sm:w-auto">PM</p>
            </div>
            <div className="space-y-3" data-testid="emar-med-list">
              {routineMeds.map((m) => {
                const e = entries[m.id] || {};
                const needsReason = e.am_status === "missed" || e.pm_status === "missed";
                return (
                  <div key={m.id} className="space-y-2">
                    <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_140px_140px] gap-2 items-center">
                      <div className="min-w-0">
                        <p className="font-display font-semibold text-sm leading-tight">{m.name}</p>
                        <p className="text-xs text-muted-foreground">{m.dosage}</p>
                      </div>
                      <div className="w-[120px] sm:w-auto"><StatusButton value={e.am_status || "none"} onClick={() => cycleStatus(m.id, "am_status")} slot="am" medId={m.id} /></div>
                      <div className="w-[120px] sm:w-auto"><StatusButton value={e.pm_status || "none"} onClick={() => cycleStatus(m.id, "pm_status")} slot="pm" medId={m.id} /></div>
                    </div>
                    {needsReason && (
                      <div className="bg-red-50 border-2 border-red-300 rounded-md p-3" data-testid={`missed-reason-block-${m.id}`}>
                        <Label className="text-label text-red-700 flex items-center gap-1.5">
                          <Info className="w-3.5 h-3.5" /> Why was it missed? (required)
                        </Label>
                        <Textarea
                          value={e.missed_reason || ""}
                          onChange={(ev) => setMissedReason(m.id, ev.target.value)}
                          rows={2}
                          placeholder="Client refused, hospital visit, vomiting, out of stock..."
                          className="mt-1 bg-white"
                          data-testid={`missed-reason-${m.id}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {routineMeds.length > 0 && (
              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-sm">
                <span><span className="font-semibold text-emerald-700">{summary.given}</span> given</span>
                <span><span className="font-semibold text-red-600">{summary.missed}</span> missed</span>
              </div>
            )}
          </Card>

          {prnMeds.length > 0 && (
            <Card className="p-4 space-y-3" data-testid="emar-prn-section">
              <div className="flex items-center justify-between">
                <p className="text-label">PRN (As-Needed) Administered</p>
                <Button type="button" variant="outline" size="sm" onClick={addPrn} data-testid="add-prn"><Plus className="w-4 h-4 mr-1" /> Log PRN</Button>
              </div>
              {prnEntries.length === 0 && (
                <p className="text-xs text-muted-foreground">Tap "Log PRN" if you administered any as-needed medication today.</p>
              )}
              {prnEntries.map((p) => (
                <div key={p.id} className="border-2 border-amber-200 bg-amber-50/50 rounded-md p-3 space-y-2" data-testid={`prn-entry-${p.id}`}>
                  <div className="flex items-center gap-2">
                    <Select value={p.medication_id} onValueChange={(v) => updatePrn(p.id, { medication_id: v })}>
                      <SelectTrigger className="flex-1" data-testid={`prn-med-${p.id}`}><SelectValue placeholder="PRN medication" /></SelectTrigger>
                      <SelectContent>{prnMeds.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {m.dosage}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => removePrn(p.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                  <Input type="time" value={p.time} onChange={(e) => updatePrn(p.id, { time: e.target.value })} className="bg-white" />
                  <Textarea value={p.reason} onChange={(e) => updatePrn(p.id, { reason: e.target.value })} rows={2} placeholder="Reason for administration (e.g., headache, anxiety)..." className="bg-white" data-testid={`prn-reason-${p.id}`} />
                </div>
              ))}
            </Card>
          )}

          <div className="sticky bottom-20 z-20">
            <Button onClick={submit} className="w-full h-14 text-base font-semibold shadow-lg" disabled={submitting} data-testid="submit-emar">
              {submitting ? "Submitting..." : "Submit eMAR Log"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
